import React from 'react';
import { Routes, Route, BrowserRouter, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import Home from './Home';
import About from './pages/about';
import Facilitators from './pages/facilitators';
import Tools from './pages/tools';
import Blog from './pages/blog';
import Contact from './pages/contactUs';
import Courses from './pages/training';
import Research from './pages/research';
import Accreditation from './pages/accreditation';
import Programs from './pages/programs';
import CoursePublic from './pages/coursePublic';
import CourseLesson from './pages/courseLesson';
import CourseCertificate from './pages/courseCertificate';
import CourseFinalQuiz from './pages/courseFinalQuiz';
import Login from './pages/login';
import AuthVerify from './pages/authVerify';
import MyCourses from './pages/myCourses';
import CourseWelcome from './pages/courseWelcome';
import Checkout from './pages/checkout';
import CheckoutComplete from './pages/checkoutComplete';
import GiftCourse from './pages/giftCourse';
import PilotAccess from './pages/pilotAccess';
import AdminDashboard from './pages/adminDashboard';
import PrivacyPolicy from './pages/privacyPolicy';
import TermsOfService from './pages/termsOfService';
import NotFound from './pages/notFound';
import CertificateVerify from './pages/CertificateVerify';
import { AuthProvider } from './lib/auth';

// Dev-only PDF tools — excluded from production builds automatically
// because CRA replaces process.env.NODE_ENV at build time and dead-code
// eliminates any branch that can never be true.
// eslint-disable-next-line no-unused-vars
const Playbook  = process.env.NODE_ENV !== 'production' ? require('./pages/playbook').default  : null;
// eslint-disable-next-line no-unused-vars
const Workbook  = process.env.NODE_ENV !== 'production' ? require('./pages/workbook').default  : null;
// eslint-disable-next-line no-unused-vars
const Workbook2 = process.env.NODE_ENV !== 'production' ? require('./pages/workbook2').default : null;
// eslint-disable-next-line no-unused-vars
const Kindle    = process.env.NODE_ENV !== 'production' ? require('./pages/kindle').default    : null;

// Routes that use their own CoursePlayerHeader — suppress the site Header/Footer.
const PLAYER_PATTERNS = [
  /^\/courses\/[^/]+\/[^/]+\/[^/]+/,
  /^\/courses\/[^/]+\/final-quiz/,
  /^\/courses\/[^/]+\/certificate/,
];

// Routes that suppress only the Footer (Header stays visible).
const NO_FOOTER_PATTERNS = [/^\/verify\//];

function isPlayerRoute(pathname) {
  return PLAYER_PATTERNS.some((re) => re.test(pathname));
}

function isNoFooterRoute(pathname) {
  return NO_FOOTER_PATTERNS.some((re) => re.test(pathname));
}

function AppShell() {
  const location = useLocation();
  const player = isPlayerRoute(location.pathname);

  return (
    <div className="flex flex-col min-h-screen print:min-h-0 print:block bg-bone text-ink">
      <ScrollToTop />
      {!player && <Header />}
      <main className="flex-grow flex flex-col print:flex-none print:block">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
          <Route path="/my-courses" element={<MyCourses />} />
          <Route path="/about" element={<About />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:slug" element={<CoursePublic />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/complete" element={<CheckoutComplete />} />
          <Route path="/courses/:slug/gift" element={<GiftCourse />} />
          <Route path="/courses/ai-for-parents/pilot-access" element={<PilotAccess />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/courses/:slug/welcome" element={<CourseWelcome />} />
          <Route path="/courses/:slug/certificate" element={<CourseCertificate />} />
          <Route path="/courses/:slug/final-quiz" element={<CourseFinalQuiz />} />
          <Route path="/courses/:slug/:moduleSlug/:lessonSlug" element={<CourseLesson />} />
          <Route path="/training" element={<Courses />} />
          <Route path="/research" element={<Research />} />
          <Route path="/accreditation" element={<Accreditation />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/tools" element={<Tools />} />

          <Route path="/facilitators" element={<Facilitators />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contactUs" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/verify/:code" element={<CertificateVerify />} />
          {Playbook  && <Route path="/playbook"  element={<Playbook />} />}
          {Workbook  && <Route path="/workbook"  element={<Workbook />} />}
          {Workbook2 && <Route path="/workbook2" element={<Workbook2 />} />}
          {Kindle    && <Route path="/kindle"    element={<Kindle />} />}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!player && !isNoFooterRoute(location.pathname) && <Footer />}
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      {/* AuthProvider must be inside BrowserRouter so child pages can use
          useNavigate/useLocation alongside useAuth. */}
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
